import i18n from './translations';
// Circular on purpose (track.js reads the token from here); both only use the
// other's functions at call time.
import { track } from './activity/track';

const TOKEN_KEY = 'jwt_token';
const USER_KEY = 'user_data';
const API_BASE_KEY = 'api_base_url';

/**
 * The backend this build belongs to — where every login starts.
 * In production (with nginx proxy), this is empty so requests go to /api/...
 * In local development, set REACT_APP_BACKEND_URL=http://localhost:5000 in .env
 */
export const HOME_API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const readStoredApiBase = () => {
  try { return localStorage.getItem(API_BASE_KEY) || ''; } catch (e) { return ''; }
};

/**
 * Base URL for all API requests of the logged-in session.
 *
 * Login switch: the home backend can hand a user to another instance
 * ({login_at} in the /api/login answer — prod sends Marcuard Heritage users
 * to the dev instance, where their data lives). Login.js then logs in there,
 * stores that instance here and reloads, so every module (many build their
 * URLs from this constant at load time) talks to it directly. Without a
 * session it is always the home backend.
 */
export const API_BASE_URL = (localStorage.getItem(TOKEN_KEY) && readStoredApiBase()) || HOME_API_BASE_URL;

/** Remember which backend the new session belongs to ('' = home). */
export const setApiBase = (base) => {
  try {
    if (base && base !== HOME_API_BASE_URL) localStorage.setItem(API_BASE_KEY, base);
    else localStorage.removeItem(API_BASE_KEY);
  } catch (e) { /* storage blocked: the session then stays on the home backend */ }
};

export const setToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  try { localStorage.removeItem(API_BASE_KEY); } catch (e) { /* ignore */ }
};

/** Decode a JWT payload (no signature check — only to read `exp`). */
const decodeJwtPayload = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64).split('').map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`).join('')
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
};

/** True only if a token exists and is already past its `exp`. */
export const isTokenExpired = () => {
  const token = getToken();
  if (!token) return false;                    // not logged in ≠ expired
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return false;  // unreadable → let the server decide
  return Date.now() >= payload.exp * 1000;
};

// ── Global "session ended" signal ──
// App registers a handler; the fetch interceptor (and the SSE helpers) fire it
// on any 401 from our API while a token is present, so an expired session
// bounces the user to login instead of failing silently.
let onUnauthorized = null;
export const setUnauthorizedHandler = (cb) => { onUnauthorized = cb; };
export const notifyUnauthorized = () => { if (getToken() && onUnauthorized) onUnauthorized(); };

let interceptorInstalled = false;
export const installAuthInterceptor = () => {
  if (interceptorInstalled || typeof window === 'undefined' || !window.fetch) return;
  interceptorInstalled = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await orig(...args);
    try {
      if (res.status === 401 && getToken()) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        // Only our own session's backend ends the session: the admin console
        // also talks to OTHER instances (admin/client.js), whose 401 just
        // means that connection expired.
        const ownApi = API_BASE_URL
          ? url.startsWith(`${API_BASE_URL}/api/`)
          : url.startsWith('/api/') || url.startsWith(`${window.location.origin}/api/`);
        if (ownApi && !url.includes('/api/login')) notifyUnauthorized();
      }
    } catch (e) { /* never let interception break a request */ }
    return res;
  };
};

export const setUser = (user) => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const getUser = () => {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

/**
 * Returns headers object with Authorization bearer token.
 * Merges with any additional headers provided.
 */
export const getAuthHeaders = (extraHeaders = {}) => {
  const token = getToken();
  return {
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...extraHeaders,
  };
};

/**
 * Appends the JWT token as a query parameter to a URL.
 * Used for EventSource connections which don't support custom headers.
 * Also prepends API_BASE_URL for local development support.
 */
export const appendTokenToUrl = (url) => {
  const fullUrl = `${API_BASE_URL}${url}`;
  const token = getToken();
  if (!token) return fullUrl;
  const separator = fullUrl.includes('?') ? '&' : '?';
  return `${fullUrl}${separator}token=${encodeURIComponent(token)}`;
};

// ── Authenticated file links (theScreener reports, ARE screener factsheet) ──
// These routes sit behind @token_required. A plain <a href> or window.open()
// navigation cannot carry the Authorization header, and putting the JWT into
// the URL leaks it into history, proxy logs and the PDF viewer's address bar.
// So such links are fetched with the header and shown from a blob URL instead.
// Legacy `/api/pdfs/...` links are deliberately NOT matched: they stay plain
// links (the backend answers them with an explanatory 410 page).
// Both are matched against the NORMALIZED API path (see protectedApiPath), and
// anchored at its start: the check must cover exactly the request that is sent.
const ARE_SCREENER_PDF_RE = /^\/api\/are\/reports\/[^/?#]+\/screener\.pdf(?:[?#]|$)/;
const PROTECTED_PATH_RE = /^\/api\/(?:thescreener\/|are\/reports\/[^/?#]+\/screener\.pdf(?:[?#]|$))/;

// The ARE screener route redirects to theScreener's public site when it has
// no report for a title. A fetch cannot follow that (cross-origin, and the
// Location of a manual redirect is hidden), so the tab is sent there directly.
const THESCREENER_SITE_URL = 'https://www.thescreener.com/';

// How long a PDF's blob URL stays valid. The viewer has loaded the bytes long
// before this; revoking frees the memory the blob pins.
const BLOB_URL_TTL_MS = 60 * 1000;

// The backend emits ABSOLUTE links ({BASE_URL}/api/...) so they also work in
// the voice-mode artifact extraction. Reducing them to the path and prefixing
// API_BASE_URL makes the same link work locally and in production, and it
// guarantees the token is only ever sent to our own API, never to whatever
// host a (model-written) link names.
const toApiPath = (url) => {
  const i = url.indexOf('/api/');
  return i >= 0 ? url.slice(i) : null;
};

// The API path (path + query) the authenticated fetch will request for `url`,
// or null when that request would not be a protected file route. The path is
// normalized the way fetch() does it (dot segments, '%2e%2e', backslashes)
// BEFORE the anchored check, so "…/api/users/1?next=/api/thescreener/" or
// "/api/thescreener/../users/1" can never send the token to another route.
const protectedApiPath = (url) => {
  if (typeof url !== 'string') return null;
  const raw = toApiPath(url);
  if (!raw) return null;
  let path;
  try {
    const parsed = new URL(raw, 'http://api.invalid');
    path = `${parsed.pathname}${parsed.search}`;
  } catch (e) {
    return null;
  }
  return PROTECTED_PATH_RE.test(path) ? path : null;
};

/** True if `url` points at a file route that needs the Authorization header. */
export const isProtectedFileUrl = (url) => protectedApiPath(url) !== null;

// Backend error codes ({error, code}) → translation keys. The status-based
// fallback covers responses without a JSON body (proxy errors, timeouts).
const FILE_ERROR_KEYS = {
  not_available: 'file_error_not_available',
  temporarily_unavailable: 'file_error_temporarily_unavailable',
  rate_limited: 'file_error_rate_limited',
  upstream_misconfigured: 'file_error_upstream_misconfigured',
  bad_request: 'file_error_bad_request',
};
const FILE_ERROR_KEYS_BY_STATUS = {
  400: 'file_error_bad_request',
  403: 'file_error_not_available',
  404: 'file_error_not_available',
  410: 'file_error_not_available',
  429: 'file_error_rate_limited',
  502: 'file_error_upstream_misconfigured',
  503: 'file_error_temporarily_unavailable',
  504: 'file_error_temporarily_unavailable',
};

const fileErrorMessage = (key) => i18n.t(key || 'file_error_generic');

const filenameFromDisposition = (header) => {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch (e) { /* fall through */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
};

// Shown in the freshly opened tab while the file loads — a cold sector report
// can take >10 s upstream, and a blank white tab reads as "broken".
const showLoadingPlaceholder = (win) => {
  try {
    const doc = win.document;
    const text = i18n.t('file_loading');
    doc.title = text;
    doc.body.style.cssText = 'margin:0;height:100vh;display:flex;align-items:center;'
      + 'justify-content:center;font:15px system-ui,-apple-system,sans-serif;color:#666;background:#f7f7f7;';
    doc.body.textContent = text;
  } catch (e) { /* placeholder is cosmetic only */ }
};

const closeWindow = (win) => {
  try { if (win && !win.closed) win.close(); } catch (e) { /* ignore */ }
};

// Popup blocked (no window to navigate): save the file instead of losing it.
const downloadBlob = (blob, filename) => {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), BLOB_URL_TTL_MS);
};

/**
 * Open a protected file (PDF) in a new tab, authenticated via header.
 *
 * MUST be called synchronously from the click handler: the tab is opened
 * right away (a window opened after an `await` is caught by popup blockers)
 * and only then filled with the fetched PDF via a blob URL.
 *
 * options:
 *   onError(message, code) — show the localized error yourself (e.g. in a
 *                            Snackbar); defaults to window.alert.
 *   fallbackUrl            — public page to show if the route answers with a
 *                            redirect (the ARE screener route sends titles it
 *                            has no report for to theScreener's site).
 *   filename               — download name if the popup was blocked.
 * Resolves to true when the file was shown, false otherwise. A 401 is
 * routed to the app's session-expired handling (back to login), no message.
 */
export const openAuthenticatedFile = (url, { onError, fallbackUrl, filename } = {}) => {
  const report = (key, code) => {
    const message = fileErrorMessage(key);
    try {
      if (onError) onError(message, code);
      else window.alert(message);
    } catch (e) { /* a broken error sink must not turn into a second error */ }
  };

  // Only ever a protected file route of our own API gets the token.
  const path = protectedApiPath(url);
  if (!path) { report('file_error_bad_request', 'bad_request'); return Promise.resolve(false); }
  if (isTokenExpired()) { notifyUnauthorized(); return Promise.resolve(false); }

  const win = window.open('', '_blank');
  if (win) {
    try { win.opener = null; } catch (e) { /* ignore */ }
    showLoadingPlaceholder(win);
  }
  track('file_open', {
    area: path.includes('thescreener') || path.includes('screener') ? 'screener' : 'files',
    title: filename || decodeURIComponent(path.split('?')[0].split('/').pop() || ''),
    detail: { type: 'pdf', path: path.split('?')[0].slice(0, 160) },
  });

  const fail = (key, code) => { closeWindow(win); report(key, code); return false; };

  // `redirect: 'manual'`: following the ARE route's redirect to theScreener's
  // site would be a cross-origin fetch that fails as an opaque network error.
  return fetch(`${API_BASE_URL}${path}`, {
    // Errors come back as JSON ({error, code}), hence the second type.
    headers: getAuthHeaders({ Accept: 'application/pdf, application/json;q=0.9' }),
    redirect: 'manual',
  })
    .then(async (res) => {
      if (res.type === 'opaqueredirect') {
        const target = fallbackUrl || (ARE_SCREENER_PDF_RE.test(path) ? THESCREENER_SITE_URL : null);
        if (!target) return fail('file_error_not_available', 'redirect');
        if (!win) { window.open(target, '_blank', 'noopener,noreferrer'); return true; }
        if (win.closed) return false;                 // user closed the tab meanwhile
        win.location.href = target;
        return true;
      }
      if (res.status === 401) {
        // The session is over: App's handler clears it and shows the login
        // with the "session expired" notice (the fetch interceptor usually
        // got there first; calling again is a no-op).
        closeWindow(win);
        notifyUnauthorized();
        return false;
      }
      const type = (res.headers.get('Content-Type') || '').toLowerCase();
      if (!res.ok) {
        let code = null;
        if (type.includes('application/json')) {
          try { code = (await res.json()).code || null; } catch (e) { /* no body */ }
        }
        return fail(FILE_ERROR_KEYS[code] || FILE_ERROR_KEYS_BY_STATUS[res.status], code || `http_${res.status}`);
      }
      // Only PDFs are rendered: a blob URL shares our origin, so showing e.g.
      // an HTML body from it would run that markup with the app's privileges.
      if (!type.includes('application/pdf')) return fail('file_error_generic', 'unexpected_type');
      const blob = await res.blob();
      if (win && win.closed) return false;           // user closed the tab meanwhile
      if (!win) {
        downloadBlob(blob, filename || filenameFromDisposition(res.headers.get('Content-Disposition')) || 'theScreener-Report.pdf');
        return true;
      }
      const blobUrl = URL.createObjectURL(blob);
      win.location.href = blobUrl;
      setTimeout(() => URL.revokeObjectURL(blobUrl), BLOB_URL_TTL_MS);
      return true;
    })
    .catch(() => fail('file_error_temporarily_unavailable', 'network_error'));
};

/**
 * Delegated click handler for containers that render model-written HTML
 * (markdown links, restored history): intercepts clicks on <a> elements whose
 * href is a protected file and opens them via openAuthenticatedFile. Other
 * links keep their default behaviour. Wire it to both onClick and onAuxClick
 * (middle click would otherwise open the unauthenticated URL in a new tab).
 * Returns true if the event was handled.
 */
export const interceptProtectedLinkClick = (event, options) => {
  if (!event || event.defaultPrevented) return false;
  if (event.type === 'auxclick' && event.button !== 1) return false;
  const target = event.target;
  const anchor = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;
  if (!anchor) return false;
  const href = anchor.getAttribute('href');
  if (!isProtectedFileUrl(href)) return false;
  event.preventDefault();
  openAuthenticatedFile(href, options);
  return true;
};
